import { Link, router } from "@inertiajs/react"
import { ArrowDown, ArrowUp } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import { cn } from "@/lib/utils"

import {
  LastFollowUpCell,
  PodCell,
  StatusChip,
  WaitingOnCell,
} from "./status-chip"
import type { WorkOrderFilters, WorkOrderRow } from "./types"

/**
 * The queue. It answers all four of the questions the product was bought for on
 * one line: what is it, who are we waiting on, how long, and when did we last
 * chase them.
 *
 * Default order is longest-wait-first (the server sorts; this only renders the
 * indicator), because the row an operator needs is almost never the newest one.
 */
const SORTS = [
  { key: "days_waiting", label: "Days waiting" },
  { key: "car", label: "Car" },
  { key: "status", label: "Status" },
  { key: "last_activity", label: "Last activity" },
] as const

export function WorkOrderTable({
  workOrders,
  filters,
  path,
}: {
  workOrders: WorkOrderRow[]
  filters: WorkOrderFilters
  path: string
}) {
  const routes = useOrgRoutes()

  function sortBy(key: string) {
    router.get(
      path,
      { ...stripped(filters), sort: key },
      { preserveState: true, preserveScroll: true },
    )
  }

  if (workOrders.length === 0) {
    return (
      <div className="text-muted-foreground rounded-lg border border-dashed p-12 text-center text-sm">
        No work orders match these filters.
      </div>
    )
  }

  return (
    <div className="overflow-x-auto rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableHead
              label="Car"
              sortKey="car"
              current={filters.sort}
              onSort={sortBy}
            />
            <TableHead>Type</TableHead>
            <TableHead>Defect / reason</TableHead>
            <TableHead>Facility</TableHead>
            <TableHead>Shop</TableHead>
            <SortableHead
              label="Status"
              sortKey="status"
              current={filters.sort}
              onSort={sortBy}
            />
            <SortableHead
              label="Waiting on"
              sortKey="days_waiting"
              current={filters.sort}
              onSort={sortBy}
            />
            <SortableHead
              label="Last follow-up"
              sortKey="last_activity"
              current={filters.sort}
              onSort={sortBy}
            />
            <TableHead>POD</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {workOrders.map((row) => (
            <TableRow
              key={row.id}
              className={cn(row.pod_missed && "bg-destructive/5")}
            >
              <TableCell className="font-medium">
                <Link
                  href={routes.workOrderPath(row.id)}
                  className="hover:underline"
                >
                  {row.car_mark_number ?? `WO #${row.id}`}
                </Link>
                {row.review_status === "pending_review" && (
                  <Badge variant="secondary" className="ml-2">
                    Pending review
                  </Badge>
                )}
              </TableCell>
              <TableCell>
                <Badge variant="outline">{row.type_label}</Badge>
              </TableCell>
              <TableCell className="text-muted-foreground max-w-64 truncate text-sm">
                {row.summary ?? "—"}
              </TableCell>
              <TableCell className="text-sm">
                {row.facility_name ?? "—"}
              </TableCell>
              <TableCell className="text-sm">{row.shop_name ?? "—"}</TableCell>
              <TableCell>
                <StatusChip status_label={row.status_label} tone={row.tone} />
              </TableCell>
              <TableCell>
                <WaitingOnCell
                  waiting_on_label={row.waiting_on_label}
                  days_waiting={row.days_waiting}
                />
              </TableCell>
              <TableCell>
                <LastFollowUpCell value={row.last_followed_up_at} />
              </TableCell>
              <TableCell>
                <PodCell
                  pod_promised_on={row.pod_promised_on}
                  pod_missed={row.pod_missed}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function SortableHead({
  label,
  sortKey,
  current,
  onSort,
}: {
  label: string
  sortKey: string
  current: string
  onSort: (key: string) => void
}) {
  const active = current === sortKey
  const sort = SORTS.find((s) => s.key === sortKey)

  return (
    <TableHead>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          "flex items-center gap-1 hover:underline",
          active && "text-foreground font-semibold",
        )}
        aria-label={`Sort by ${sort?.label ?? label}`}
      >
        {label}
        {active &&
          (sortKey === "days_waiting" ? (
            <ArrowDown className="size-3" />
          ) : (
            <ArrowUp className="size-3" />
          ))}
      </button>
    </TableHead>
  )
}

function stripped(filters: WorkOrderFilters): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [key, value] of Object.entries(filters)) {
    if (value != null && value !== "" && key !== "sort")
      out[key] = String(value)
  }
  return out
}
